import React from "react";
import { Card, Typography, Space, Statistic, Row, Col, Alert, Tag } from "antd";
import { BarChart3, TrendingUp, DollarSign, Calendar, Target } from "lucide-react";

const { Title, Text } = Typography;

interface Step2Props {
  experimentData?: any;
  powerResults?: any;
}

const Step2CalculatePower: React.FC<Step2Props> = ({ experimentData, powerResults }) => {
  if (!powerResults) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card className="text-center py-12">
          <Title level={3}>No Results Available</Title>
          <Text type="secondary">
            Please complete Step 1 and click "Calculate Power" to see results
          </Text>
        </Card>
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <BarChart3 className="w-8 h-8 text-blue-600" />
          <Title level={2} className="mb-0">GeoLift Power Analysis Results</Title>
        </div>
        <Text type="secondary" className="text-base">
          Backend analysis complete for test market: <strong>{powerResults.location}</strong>
        </Text>
      </div>

      <Space direction="vertical" size="large" className="w-full">
        <Alert
          message="Power Analysis Complete"
          description={`Based on your test market (${powerResults.location}) and ${powerResults.duration} week duration, the analysis shows ${formatPercent(powerResults.predictedLiftPercent)} predicted lift with ${formatPercent(powerResults.power * 100)} statistical power.`}
          type="success"
          showIcon
        />

        {/* Test & Control Markets Summary */}
        <Card className="shadow-sm bg-gradient-to-br from-blue-50 to-indigo-50">
          <div className="flex justify-between items-start">
            <div>
              <Title level={4} className="mb-2">Test & Control Markets</Title>
              <Space direction="vertical" size="small">
                <div>
                  <Text strong>Test Markets: </Text>
                  {powerResults.testMarkets && powerResults.testMarkets.length > 0 ? (
                    powerResults.testMarkets.map((market: string, idx: number) => (
                      <Tag key={idx} color="blue" className="ml-1">{market}</Tag>
                    ))
                  ) : (
                    <Tag color="blue" className="ml-1">{powerResults.location}</Tag>
                  )}
                </div>
                <div>
                  <Text strong>Duration: </Text>
                  <Text>{powerResults.duration} weeks</Text>
                </div>
                <div>
                  <Text strong>CPIC: </Text>
                  <Text>${powerResults.targetCPIC.toFixed(2)} per $1 incremental sales</Text>
                </div>
              </Space>
            </div>
            <div className="text-right">
              <Statistic
                title="Statistical Power"
                value={powerResults.power * 100}
                suffix="%"
                precision={1}
                valueStyle={{ color: "#1890ff", fontSize: "2rem" }}
              />
            </div>
          </div>
        </Card>

        {/* Primary Metrics - Investment & Lift */}
        <Card className="shadow-sm">
          <Title level={4} className="mb-4">Investment & Expected Lift</Title>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <Statistic
                  title={<span className="font-semibold">Required Investment</span>}
                  value={powerResults.investment}
                  prefix={<DollarSign className="w-5 h-5 inline mr-2" />}
                  precision={0}
                  valueStyle={{ color: "#059669", fontSize: "1.75rem" }}
                />
                <Text type="secondary" className="text-sm">
                  Total investment needed for test markets
                </Text>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <Statistic
                  title={<span className="font-semibold">Predicted Lift (%)</span>}
                  value={powerResults.predictedLiftPercent}
                  suffix="%"
                  prefix={<TrendingUp className="w-5 h-5 inline mr-2" />}
                  precision={1}
                  valueStyle={{ color: "#1e40af", fontSize: "1.75rem" }}
                />
                <Text type="secondary" className="text-sm">
                  Expected percentage increase in sales
                </Text>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                <Statistic
                  title={<span className="font-semibold">Predicted Lift ($)</span>}
                  value={powerResults.avgATT}
                  prefix="$"
                  precision={0}
                  valueStyle={{ color: "#7c3aed", fontSize: "1.75rem" }}
                />
                <Text type="secondary" className="text-sm">
                  Dollar lift per period
                </Text>
              </Card>
            </Col>
          </Row>
        </Card>

        {/* Budget Analysis */}
        <Card className="shadow-sm">
          <Title level={4} className="mb-4">Budget Analysis</Title>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Statistic
                title="Campaign Budget"
                value={powerResults.campaignBudget}
                prefix="$"
                precision={0}
                valueStyle={{ color: "#000" }}
              />
              <Text type="secondary" className="text-sm">
                Total budget allocated
              </Text>
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title="BAU Budget"
                value={powerResults.bauBudget}
                prefix="$"
                precision={0}
                valueStyle={{ color: "#666" }}
              />
              <Text type="secondary" className="text-sm">
                Business as usual spending
              </Text>
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title="Incremental Budget"
                value={powerResults.incrementalBudget}
                prefix="$"
                precision={0}
                valueStyle={{ color: "#3f8600" }}
              />
              <Text type="secondary" className="text-sm">
                Additional spend for test
              </Text>
            </Col>
          </Row>
        </Card>

        {/* Experiment Design */}
        <Card className="shadow-sm">
          <Title level={4} className="mb-4">Experiment Design Parameters</Title>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Statistic
                title="Treatment Duration"
                value={powerResults.duration}
                suffix=" weeks"
                prefix={<Calendar className="w-5 h-5 inline mr-2" />}
                valueStyle={{ color: "#d97706" }}
              />
              <Text type="secondary" className="text-sm">
                Length of the test period
              </Text>
            </Col>
            <Col xs={24} md={8}>
              <Statistic
                title="Effect Size (Simulated)"
                value={powerResults.effectSize * 100}
                suffix="%"
                precision={1}
                prefix={<Target className="w-5 h-5 inline mr-2" />}
                valueStyle={{ color: "#1890ff" }}
              />
              <Text type="secondary" className="text-sm">
                Simulated lift for power calculation
              </Text>
            </Col>
            <Col xs={24} md={8}>
              <Statistic
                title="Scaled L2 Imbalance"
                value={powerResults.avgScaledL2Imbalance}
                precision={3}
                valueStyle={{ color: "#722ed1" }}
              />
              <Text type="secondary" className="text-sm">
                Measure of control market quality
              </Text>
            </Col>
          </Row>
        </Card>

        {/* Total Impact Calculation */}
        <Card className="shadow-sm bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <Title level={4} className="mb-4">Total Campaign Impact</Title>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Statistic
                title={<span className="font-semibold">Total Expected Lift</span>}
                value={powerResults.avgATT * powerResults.duration}
                prefix="$"
                precision={0}
                valueStyle={{ color: "#d97706", fontSize: "2rem" }}
              />
              <Text type="secondary" className="text-sm">
                Over {powerResults.duration} week period
              </Text>
            </Col>
            <Col xs={24} sm={12}>
              <Statistic
                title={<span className="font-semibold">Return on Investment (ROI)</span>}
                value={((powerResults.avgATT * powerResults.duration - powerResults.investment) / powerResults.investment) * 100}
                suffix="%"
                precision={1}
                valueStyle={{
                  color: ((powerResults.avgATT * powerResults.duration) > powerResults.investment) ? "#3f8600" : "#cf1322",
                  fontSize: "2rem"
                }}
              />
              <Text type="secondary" className="text-sm">
                Expected return on test investment
              </Text>
            </Col>
          </Row>
        </Card>

        {/* Key Recommendations */}
        <Alert
          message="Key Recommendations"
          description={
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>
                <strong>Investment Required:</strong> {formatCurrency(powerResults.investment)} to run this test in {powerResults.location}
              </li>
              <li>
                <strong>Expected Lift:</strong> {formatPercent(powerResults.predictedLiftPercent)} lift equates to {formatCurrency(powerResults.avgATT)} per week
              </li>
              <li>
                <strong>Statistical Power:</strong> {formatPercent(powerResults.power * 100)} power means you have a good chance of detecting the effect
              </li>
              <li>
                <strong>Duration:</strong> Run the test for {powerResults.duration} weeks to achieve reliable results
              </li>
              {powerResults.avgScaledL2Imbalance < 0.1 && (
                <li className="text-green-700">
                  <strong>Control Quality:</strong> Excellent control market match (L2 Imbalance: {powerResults.avgScaledL2Imbalance.toFixed(3)})
                </li>
              )}
            </ul>
          }
          type="info"
          showIcon
        />
      </Space>
    </div>
  );
};

export default Step2CalculatePower;
